import React, { useState, useRef, useEffect } from 'react'
import './MentionInput.css'

const MentionInput = ({ value, onChange, onSubmit, ticketId, placeholder = "Saisissez un commentaire..." }) => {
    const [suggestions, setSuggestions] = useState([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [mentionQuery, setMentionQuery] = useState('')
    const [cursorPosition, setCursorPosition] = useState(0)
    const textareaRef = useRef(null)

    useEffect(() => {
        const fetchSuggestions = async () => {
            if (mentionQuery.length >= 2) {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/tickets/${ticketId}/search-users?q=${mentionQuery}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                'Accept': 'application/json',
                            }
                        }
                    )
                    const users = await response.json()

                    // Ajouter @all comme première option
                    const allOption = { id: 'all', name: 'all', email: 'Notifier tout le monde', role: 'option' }
                    setSuggestions([allOption, ...users])
                    setShowSuggestions(true)
                } catch (error) {
                    console.error('Erreur lors de la recherche:', error)
                    setSuggestions([])
                }
            } else if (mentionQuery === '') {
                // Afficher @all uniquement
                setSuggestions([{ id: 'all', name: 'all', email: 'Notifier tout le monde', role: 'option' }])
                setShowSuggestions(true)
            } else {
                setSuggestions([])
                setShowSuggestions(false)
            }
        }

        if (mentionQuery !== null) {
            fetchSuggestions()
        }
    }, [mentionQuery, ticketId])

    const handleChange = (e) => {
        const newValue = e.target.value
        const position = e.target.selectionStart

        onChange(newValue)
        setCursorPosition(position)

        // Détecter si on est en train de taper une mention
        const textBeforeCursor = newValue.substring(0, position)
        const lastAtIndex = textBeforeCursor.lastIndexOf('@')

        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1)
            // Vérifier que c'est bien le début d'une mention (pas d'espace avant @)
            const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' '

            if ((charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0) && !textAfterAt.includes(' ')) {
                setMentionQuery(textAfterAt)
            } else {
                setMentionQuery(null)
                setShowSuggestions(false)
            }
        } else {
            setMentionQuery(null)
            setShowSuggestions(false)
        }
    }

    const insertMention = (user) => {
        const textBeforeCursor = value.substring(0, cursorPosition)
        const textAfterCursor = value.substring(cursorPosition)
        const lastAtIndex = textBeforeCursor.lastIndexOf('@')

        const beforeMention = value.substring(0, lastAtIndex)
        const mention = `@${user.name}`
        const newValue = beforeMention + mention + ' ' + textAfterCursor

        onChange(newValue)
        setShowSuggestions(false)
        setMentionQuery(null)

        // Remettre le focus sur le textarea
        setTimeout(() => {
            textareaRef.current?.focus()
            const newPosition = (beforeMention + mention + ' ').length
            textareaRef.current?.setSelectionRange(newPosition, newPosition)
        }, 10)
    }

    const handleKeyDown = (e) => {
        if (showSuggestions && suggestions.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setSelectedIndex((prev) => (prev + 1) % suggestions.length)
            } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length)
            } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (showSuggestions && suggestions[selectedIndex]) {
                    insertMention(suggestions[selectedIndex])
                } else if (onSubmit) {
                    onSubmit(e)
                }
            } else if (e.key === 'Escape') {
                setShowSuggestions(false)
            }
        } else if (e.key === 'Enter' && !e.shiftKey && onSubmit) {
            e.preventDefault()
            onSubmit(e)
        }
    }

    // Calculer la position du dropdown
    const getDropdownStyle = () => {
        return {
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            marginBottom: '5px',
            zIndex: 1000
        }
    }

    return (
        <div style={{ position: 'relative' }}>
            {showSuggestions && suggestions.length > 0 && (
                <div className="mention-suggestions" style={getDropdownStyle()}>
                    {suggestions.map((user, index) => (
                        <div
                            key={user.id}
                            className={`mention-suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                            onClick={() => insertMention(user)}
                            onMouseEnter={() => setSelectedIndex(index)}
                        >
                            <div className="d-flex align-items-center">
                                <div className="avatar-xs me-2">
                                    <span className="avatar-title rounded-circle bg-soft-primary text-primary">
                                        {user.name.charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                <div className="flex-grow-1">
                                    <div className="fw-bold">@{user.name}</div>
                                    <small className="text-muted">{user.email}</small>
                                </div>
                                {user.role && user.role !== 'option' && (
                                    <span className={`badge bg-soft-${user.role === 'admin' ? 'danger' : user.role === 'manager' ? 'warning' : 'info'} ms-2`}>
                                        {user.role}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <textarea
                ref={textareaRef}
                className="form-control border-primary"
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                rows={3}
            />
            <div className="mt-2">
                <small className="text-muted">
                    <i className="mdi mdi-at me-1"></i>
                    Utilisez <strong>@</strong> pour mentionner quelqu'un ou <strong>@all</strong> pour tout le monde
                </small>
            </div>
        </div>
    )
}

export default MentionInput
